import { formatLocalDate, formatLocalDateTime } from '../utils/localDateTime';
import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import type { Organization } from '@types';
import { db } from '../../firebaseConfig';
import { clientDashboardCountries } from '../utils/clientDashboardCountries';

type Brief = { id: string; country: string; data?: any; loading: boolean; error?: boolean };
type ArchivedBrief = { id: string; data: any };

function coverage(organization: Organization, deliveredCountries: string[]): Brief[] {
    return clientDashboardCountries(organization, deliveredCountries).map(country => ({
        id: country.iso3, country: country.name, loading: true
    }));
}

function publishedAt(value: any): string {
    return formatLocalDateTime(value, 'Date unavailable');
}

const DailyCountryBriefs: React.FC<{
    organization: Organization;
    deliveredCountries?: string[];
    countryIso3?: string;
    placement?: 'card' | 'header';
    onOpen: (report: any, country: string) => void;
}> = ({ organization, deliveredCountries = [], countryIso3, placement = 'card', onOpen }) => {
    const inHeader = placement === 'header';
    const countries = useMemo(() => coverage(organization, deliveredCountries).filter(country => !countryIso3 || country.id === countryIso3),
        [organization.headquartersAddress, organization.additionalLocations, deliveredCountries.join('|'), countryIso3]);
    const [briefs, setBriefs] = useState<Record<string, Brief>>({});
    const [archiveCountry, setArchiveCountry] = useState<Brief | null>(null);
    const [archive, setArchive] = useState<ArchivedBrief[]>([]);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [archiveError, setArchiveError] = useState(false);

    useEffect(() => {
        setBriefs(Object.fromEntries(countries.map(country => [country.id, country])));
        const unsubscribers = countries.map(country => onSnapshot(
            doc(db, 'daily_country_analysis', country.id),
            snapshot => setBriefs(previous => ({ ...previous, [country.id]: {
                ...country, data: snapshot.exists() && snapshot.data().status === 'success' ? snapshot.data() : undefined,
                loading: false
            } })),
            () => setBriefs(previous => ({ ...previous, [country.id]: { ...country, loading: false, error: true } }))
        ));
        return () => unsubscribers.forEach(unsubscribe => unsubscribe());
    }, [countries]);

    const openArchive = async (country: Brief) => {
        setArchiveCountry(country);
        setArchive([]);
        setArchiveError(false);
        setArchiveLoading(true);
        try {
            const reports = await getDocs(query(
                collection(db, 'daily_country_analysis', country.id, 'reports'),
                orderBy('reportDate', 'desc'), limit(30)
            ));
            setArchive(reports.docs.filter(report => report.data().status === 'success')
                .map(report => ({ id: report.id, data: report.data() })));
        } catch {
            setArchiveError(true);
        } finally {
            setArchiveLoading(false);
        }
    };

    if (!countries.length)
        return null;

    return (
        <section className={inHeader ? 'rounded-xl border border-white/20 bg-white/10 p-5' : 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm'} aria-label="Daily country briefs">
            <div className={inHeader ? 'mb-2' : 'mb-4'}>
                <h2 className={`text-lg font-bold ${inHeader ? 'text-white' : 'text-slate-900'}`}>{countryIso3 ? 'Daily Micro Report' : 'Daily Country Micro Reports'}</h2>
                <p className={`text-sm ${inHeader ? 'text-slate-300' : 'text-slate-600'}`}>Latest report and past briefs for this country.</p>
            </div>
            <div className={inHeader ? 'space-y-2' : 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3'}>
                {countries.map(country => {
                    const brief = briefs[country.id] || country;
                    return <div key={country.id} id={`daily-brief-${encodeURIComponent(country.country)}`} className={inHeader ? '' : 'rounded-xl border border-slate-200 bg-slate-50 p-4'}>
                        {!inHeader && <h3 className="font-semibold text-slate-900">{country.country}</h3>}
                        <p className={`mt-1 text-xs ${inHeader ? 'text-slate-300' : 'text-slate-500'}`}>
                            {brief.loading ? 'Loading report…' : brief.error ? 'Report unavailable' : brief.data ? `Published ${publishedAt(brief.data.lastUpdated)}` : 'Awaiting first report'}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-3 text-sm font-semibold">
                            {brief.data && <button type="button" className={inHeader ? 'text-white underline hover:text-indigo-200' : 'text-blue-700 hover:underline'} onClick={() => onOpen(brief.data, country.country)}>View report</button>}
                            <button type="button" className={inHeader ? 'text-indigo-200 underline hover:text-white' : 'text-slate-600 hover:underline'} onClick={() => void openArchive(country)}>Past briefs</button>
                        </div>
                    </div>;
                })}
            </div>
            {archiveCountry && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" onClick={() => setArchiveCountry(null)}>
                <div role="dialog" aria-modal="true" aria-label={`${archiveCountry.country} past briefs`} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={event => event.stopPropagation()}>
                    <div className="mb-4 flex items-center justify-between gap-4">
                        <h3 className="text-lg font-bold text-slate-900">{archiveCountry.country} Past Briefs</h3>
                        <button className="text-sm font-semibold text-slate-600 hover:underline" onClick={() => setArchiveCountry(null)}>Close</button>
                    </div>
                    {archiveLoading ? <p className="text-sm text-slate-600">Loading briefs…</p>
                        : archiveError ? <p className="text-sm text-red-700">Could not load past briefs.</p>
                        : archive.length ? <div className="max-h-96 space-y-2 overflow-y-auto">
                            {archive.map(report => <button key={report.id} className="w-full rounded-lg border border-slate-200 p-3 text-left text-sm font-semibold text-blue-700 hover:bg-slate-50" onClick={() => { setArchiveCountry(null); onOpen(report.data, archiveCountry.country); }}>
                                {formatLocalDate(report.data.reportDate, report.id)}{report.data.timeZone ? ` · ${report.data.timeZone}` : ''} · View report
                            </button>)}
                        </div> : <p className="text-sm text-slate-600">Past briefs appear after the next daily run.</p>}
                </div>
            </div>}
        </section>
    );
};

export default DailyCountryBriefs;
